import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { MatDialog } from '@angular/material/dialog';
import { SessionService } from './session.service';
import { getSsoProviderName, getSsoProviderNameForEmail, isSsoConfigured } from './amplify-config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public loggedUser: any;

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private dialog: MatDialog
  ) {}

  /** Auth Related Methods - starts */
  public getToken(): string {
    return sessionStorage.getItem('token') || '';
  }

  public isLoggedIn() {
    const userName = this.sessionService.getUsername();
    if (!userName) {
      return false;
    }
    // Prefer sync checks — getUserAttributes() is async and cannot be used here.
    try {
      const authUser = sessionStorage.getItem('authUser');
      if (authUser) {
        return true;
      }
    } catch {
      // fall through
    }
    return !!userName;
  }
  /** Auth Related Methods - ends */

  /**
   * Starts Okta SAML 2.0 SSO via Cognito Hosted UI.
   * Cognito federates to a SAML IdP derived from the email domain
   * (dataunveil.com → DU-SSO-SCRAII; others → {DOMAIN}-SSO-SCRAII).
   * Angular never talks to Okta directly — only Cognito OAuth (authorization code).
   *
   * @param options.forceFresh sign in from scratch: whatever a previous session
   *        left behind is dropped instead of resumed, so Hosted UI authenticates
   *        this attempt. Always returns `redirecting`.
   *
   * @returns `already_authenticated` when Cognito tokens already exist (resume app session)
   *          `redirecting` when the browser is sent to Hosted UI
   */
  async loginWithOkta(
    email?: string,
    options: { forceFresh?: boolean } = {}
  ): Promise<'already_authenticated' | 'redirecting'> {
    if (!isSsoConfigured()) {
      throw new Error(
        'SSO is not configured. Set environment.cognitoDomain to your Cognito Hosted UI domain.'
      );
    }

    if (options.forceFresh) {
      // Nothing is resumed: the app session goes now, and the Cognito one goes
      // with the signOut below. Whoever was signed in before is signed out,
      // which is what makes the next screen a real Okta login.
      this.clearAppSession();
    } else {
      // Prior incomplete SSO can leave Cognito tokens while the login page is still shown.
      try {
        const existing = await fetchAuthSession();
        if (existing.tokens?.idToken) {
          await this.applyAmplifySessionToApp();
          return 'already_authenticated';
        }
      } catch {
        // No usable session — continue to Hosted UI.
      }
    }

    // Clear any half-open Amplify auth so signInWithRedirect does not throw
    // "There is already a signed in user." Globally when starting fresh, the way
    // password login signs out, so the refresh token goes and not just this
    // browser's copy of it.
    try {
      await signOut({ global: !!options.forceFresh });
    } catch {
      // ignore
    }

    const providerName = email
      ? getSsoProviderNameForEmail(email)
      : getSsoProviderName();
    const hint = (email || '').trim();
    await signInWithRedirect({
      provider: { custom: providerName },
      ...(hint ? { options: { loginHint: hint } } : {}),
    } as any);
    return 'redirecting';
  }

  /** Returns true when Cognito Hosted UI / Okta SSO is configured. */
  isOktaSsoAvailable(): boolean {
    return isSsoConfigured();
  }

  /**
   * Completes post-OAuth session setup from the Amplify Cognito session
   * (used by /callback after Hosted UI redirect).
   */
  async establishSessionFromAmplify(): Promise<{
    username: string;
    email?: string;
    attributes: Record<string, string | undefined>;
  }> {
    // Tokens may already exist (resume after Okta SSO / prior Cognito session).
    try {
      const existing = await fetchAuthSession();
      if (existing.tokens?.idToken) {
        return this.applyAmplifySessionToApp();
      }
    } catch {
      // Continue waiting for OAuth exchange.
    }
    await this.waitForOAuthSession();
    return this.applyAmplifySessionToApp();
  }

  /**
   * Maps an existing Amplify Cognito session into app sessionStorage / SessionService.
   */
  private async applyAmplifySessionToApp(): Promise<{
    username: string;
    email?: string;
    attributes: Record<string, string | undefined>;
  }> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) {
      throw new Error('No Cognito session tokens found after SSO redirect.');
    }

    const user = await getCurrentUser();
    // Federated tokens may lack aws.cognito.signin.user.admin, so GetUser
    // (fetchUserAttributes) can fail. Prefer ID token claims, then merge attributes.
    const tokenClaims = this.readIdTokenClaims(idToken.toString());
    let attributes: Record<string, string | undefined> = { ...tokenClaims };

    try {
      const fetched = await fetchUserAttributes();
      attributes = { ...attributes, ...fetched };
    } catch (err: any) {
      console.warn(
        'fetchUserAttributes unavailable for this token; using ID token claims.',
        err?.message || err
      );
    }

    const email =
      attributes['email'] ||
      tokenClaims['email'] ||
      user.signInDetails?.loginId ||
      user.username;
    const username = email || user.username;

    this.sessionService.setUser(username);
    this.sessionService.setUsername(username);
    this.sessionService.setUsernameLocal(username);
    this.sessionService.setMainUsername(username);
    this.sessionService.setUserDisplays(username);
    this.sessionService.setLoginTime(new Date().toISOString());

    const idTokenJwt = idToken.toString();
    sessionStorage.setItem('token', idTokenJwt);
    sessionStorage.setItem(
      'authUser',
      JSON.stringify({
        id: attributes['sub'] || tokenClaims['sub'] || user.userId,
        username: user.username,
        attributes,
      })
    );
    sessionStorage.removeItem('userType');

    return { username, email, attributes };
  }

  /** Decode JWT payload without verifying signature (token already from Cognito). */
  private readIdTokenClaims(
    idToken: string
  ): Record<string, string | undefined> {
    try {
      const payload = idToken.split('.')[1];
      if (!payload) {
        return {};
      }
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(json) as Record<string, unknown>;
      const result: Record<string, string | undefined> = {};
      Object.keys(claims).forEach((key) => {
        const value = claims[key];
        if (value == null) {
          result[key] = undefined;
        } else if (typeof value === 'string') {
          result[key] = value;
        } else {
          result[key] = String(value);
        }
      });
      return result;
    } catch {
      return {};
    }
  }

  /**
   * Waits until Amplify finishes exchanging the Hosted UI authorization code.
   */
  private waitForOAuthSession(timeoutMs = 15000): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;

      const finish = (err?: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        removeHub();
        if (err) {
          reject(err);
        } else {
          resolve();
        }
      };

      const removeHub = Hub.listen('auth', ({ payload }) => {
        if (payload.event === 'signInWithRedirect') {
          finish();
        }
        if (payload.event === 'signInWithRedirect_failure') {
          const data: any = payload.data;
          finish(
            new Error(
              data?.error?.message ||
                data?.message ||
                'Cognito OAuth token exchange failed.'
            )
          );
        }
      });

      const timer = setTimeout(() => {
        finish(
          new Error(
            'No Cognito session tokens found after SSO redirect. Confirm callback URL http://localhost:4200/callback is allowed on the Cognito app client, and try SSO again from the login page (do not refresh /callback).'
          )
        );
      }, timeoutMs);

      // Tokens may already exist if the OAuth listener finished before Hub was attached.
      const poll = async () => {
        for (let attempt = 0; attempt < 40 && !settled; attempt++) {
          try {
            const session = await fetchAuthSession();
            if (session.tokens?.idToken) {
              finish();
              return;
            }
          } catch {
            // keep waiting for Hub / next poll
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      };
      void poll();
    });
  }

  logoutAndRedirect() {
    signOut({ global: false }).catch(() => {});

    this.clearAppSession();
    this.dialog.closeAll();

    window.location.href = '/';
  }

  /**
   * Drops the app's own session: everything a signed-in user left in
   * `sessionStorage` and in `SessionService`.
   *
   * Amplify's Cognito tokens are not in here — `signOut` owns those — and
   * neither is `localStorage`, which is where Remember Me keeps the email and
   * password a returning user expects to find filled in.
   */
  private clearAppSession(): void {
    sessionStorage.clear();
    this.sessionService.setUser(null);
    this.sessionService.setUsername(null);
    this.sessionService.setMainUsername(null);
    this.sessionService.setLoginTime(null);
  }
}
